// public/v3/src/pages/lab-orders.jsx — the Lab Orders tab (id stays
// 'questhomebound'): the web port of the native 3-mode surface
// (ios/.../FeatureQuest/QuestRootView.swift + LabOrderFormView.swift).
//
//   1. e-Req draft   — internal unsigned Elation draft for an e-orderable
//      (compendium-linked) lab; sign/e-order in Elation → preview-EXEMPT.
//      Review step is a summary + required AOE answers from set-imported rows.
//   2. Print & fax   — requisition for a configured print-only lab; EXTERNAL
//      fax → MANDATORY real-PDF preview (app-wide preview-before-send rule):
//      /print/preview renders the actual requisition in an iframe and the
//      ONLY Save draft / Fax buttons live under the rendered document.
//   3. Quest home draw — the original homebound form (fields/validation
//      unchanged), now behind the same preview-then-send gate (the
//      side-effect-free /api/quest-homebound/preview) and with the shared
//      order-set chips row scoped to the Quest vendor.
//
// Shared across modes: the Order Chat pin-picker, per-test dx auto-resolve
// chips (POST /api/quest-homebound/resolve-dx — 404 means the practice flag
// is off → quiet manual mode), and the ORDER SETS chips row scoped to the
// current lab (hidden entirely when GET /sets fails soft).
//
// The mode CHOICE persists in localStorage (a UI pref, never PHI); form
// contents live in memory only.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ccmApi } from '../lib/api.js';
import { patientFragment, applyCanonicalName, formatDob } from '../lib/order-chat-patient-pin.js';
import { validateForm, buildPayload, FREQUENCY_OPTIONS } from '../lib/quest-homebound-form.js';
import {
  MODES, MODE_LABELS, MODE_STORAGE_KEY, sanitizeMode,
  FASTING_OPTIONS, fastingLabel,
  pickedConfidence, statusBadge, dxCandidateLabel,
  rowCodes, validateLabForm, buildOrderTests, importSetIntoRows,
  currentSetTests, requiredAOE, setsForVendor, questTestsWithChips,
} from '../lib/lab-orders-form.js';
import { confirmDialog } from '../components/confirm-dialog.jsx';
import '../styles/lab-orders.css';

let _rowSeq = 0;
const nextRowId = () => `r${++_rowSeq}`;

const emptyLabRow = () => ({
  id: nextRowId(), name: '', icd10: '',
  testId: null, code: null, compendium: null, questions: [],
  dx: null, dxResolvedForName: null, dxPending: false, chosenDx: null,
});

const emptyQuestRow = () => ({
  id: nextRowId(), name: '', testNumber: '', icd10: '', fasting: false,
  frequency: '', startDate: '', finishDate: '',
  dx: null, dxResolvedForName: null, dxPending: false, chosenDx: null,
});

// Standing-order frequency: a picker over the standard cadences with a
// Custom… escape hatch (voice orders and old drafts may carry free text —
// a non-standard value opens in the custom input rather than being lost).
// One element per grid cell, so the quest row grid is undisturbed.
function FrequencyCell({ value, disabled, onChange }) {
  const [custom, setCustom] = useState(() => !!value && !FREQUENCY_OPTIONS.includes(value));
  if (custom) {
    return (
      <input placeholder="Frequency (custom)" value={value} aria-label="Frequency" autoFocus
             onChange={(e) => onChange(e.target.value)}
             onBlur={() => { if (!(value || '').trim()) setCustom(false); }}
             disabled={disabled} />
    );
  }
  return (
    <select value={FREQUENCY_OPTIONS.includes(value) ? value : ''} aria-label="Frequency" disabled={disabled}
            onChange={(e) => {
              if (e.target.value === '__custom') { onChange(''); setCustom(true); }
              else onChange(e.target.value);
            }}>
      <option value="">Frequency…</option>
      {FREQUENCY_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
      <option value="__custom">Custom…</option>
    </select>
  );
}

// A set-imported row for the shared form: Elation test id + code/compendium/
// AOE questions ride along; dx re-resolves against the pinned chart.
const labRowFromSetTest = (test) => ({
  ...emptyLabRow(),
  name: String(test.name || '').trim(),
  testId: test.id || null,
  code: test.code || null,
  compendium: test.compendium || null,
  questions: Array.isArray(test.questions) ? test.questions : [],
});

const questRowFromSetTest = (test) => ({
  ...emptyQuestRow(),
  name: String(test.name || '').trim(),
});

function base64PdfUrl(pdfBase64) {
  try {
    const blob = new Blob([Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0))], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

function errorLines(e, fallback) {
  const body = e && e.body;
  if (body && Array.isArray(body.errors) && body.errors.length) return body.errors;
  return [(e && e.message) || fallback];
}

// ---------------------------------------------------------------------------
// Patient pin (the Order Chat pin-picker, shared by all three modes)
// ---------------------------------------------------------------------------

function usePatientPin(busy) {
  const [patientInput, setPatientInput] = useState('');
  const [pinnedPatient, setPinnedPatient] = useState(null); // { elationId, firstName, lastName, dob }
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const searchSeq = useRef(0);
  const debounce = useRef(null);

  useEffect(() => {
    const frag = patientFragment(patientInput) || patientInput.trim();
    const pinnedName = pinnedPatient ? `${pinnedPatient.firstName} ${pinnedPatient.lastName}` : '';
    if (frag.length < 2 || frag.toLowerCase() === pinnedName.toLowerCase()) {
      setSuggestions([]); setSuggestOpen(false); return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      try {
        const data = await ccmApi.searchOrderChatPatients(frag);
        if (seq !== searchSeq.current) return;
        const results = (data && data.results) || [];
        setSuggestions(results); setSuggestOpen(results.length > 0);
      } catch { if (seq === searchSeq.current) { setSuggestions([]); setSuggestOpen(false); } }
    }, 200);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [patientInput, pinnedPatient]);

  function onInput(value) {
    setPatientInput(value);
    if (pinnedPatient) setPinnedPatient(null);
  }
  function select(p) {
    setPatientInput(applyCanonicalName(patientInput, `${p.firstName} ${p.lastName}`));
    setPinnedPatient({ elationId: p.elationId, firstName: p.firstName, lastName: p.lastName, dob: p.dob });
    setSuggestions([]); setSuggestOpen(false);
  }
  function clear() { setPinnedPatient(null); }

  const view = (
    <div className="field laborders-patient">
      <div className="field-label">Patient</div>
      <input type="text" value={patientInput} placeholder="Type a name…"
             onChange={(e) => onInput(e.target.value)} disabled={busy} />
      <ul className="orderchat-suggest" role="listbox" hidden={!suggestOpen || suggestions.length === 0}>
        {suggestions.map((p) => (
          <li key={p.directoryId} role="option" className="orderchat-suggest-item"
              onMouseDown={(e) => { e.preventDefault(); select(p); }}>
            <span className="orderchat-suggest-name">{p.firstName} {p.lastName}</span>
            {p.dob ? <span className="orderchat-suggest-dob">DOB {formatDob(p.dob)}</span> : null}
          </li>
        ))}
      </ul>
      {pinnedPatient && (
        <div className="orderchat-pin">
          <span aria-hidden="true">📌</span>
          <strong>{pinnedPatient.firstName} {pinnedPatient.lastName}</strong>
          {pinnedPatient.dob ? <span className="orderchat-pin-dob">· DOB {formatDob(pinnedPatient.dob)}</span> : null}
          <button type="button" className="orderchat-pin-x" aria-label="Clear pinned patient" disabled={busy} onClick={clear}>✕</button>
        </div>
      )}
    </div>
  );

  return { pinnedPatient, clearPin: clear, view };
}

// ---------------------------------------------------------------------------
// Per-row dx auto-resolve (resolve-dx — QuestModel/LabOrderModel parity)
// ---------------------------------------------------------------------------

// Debounce-batch every row whose (non-empty) name has no answer yet, resolve
// them via /api/quest-homebound/resolve-dx (max 9 names per call, chunked),
// and write the chips back only where the name still matches. A 404 means the
// practice's auto-dx flag is off → chips hide, the form stays fully manual.
function useDxResolver(pinnedPatient, rows, setRows, onForbidden) {
  const [dxAvailable, setDxAvailable] = useState(true);
  const timer = useRef(null);
  const generation = useRef(0);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const elationId = pinnedPatient ? pinnedPatient.elationId : null;

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const schedule = useCallback((rows) => {
    if (timer.current) clearTimeout(timer.current);
    if (!dxAvailable || !elationId) return;
    const stale = [];
    for (const row of rows) {
      const name = String(row.name || '').trim();
      if (!name || name === row.dxResolvedForName) continue;
      stale.push({ id: row.id, name });
    }
    // Clear rows whose names went blank or changed; mark the stale ones pending.
    setRows(rs => rs.map(r => {
      const name = String(r.name || '').trim();
      if (!name) {
        return (r.dx || r.chosenDx || r.dxPending)
          ? { ...r, dx: null, dxResolvedForName: null, dxPending: false, chosenDx: null }
          : r;
      }
      if (name !== r.dxResolvedForName) {
        return { ...r, dx: null, dxResolvedForName: null, dxPending: true, chosenDx: null };
      }
      return r;
    }));
    if (!stale.length) return;
    const gen = generation.current;
    timer.current = setTimeout(async () => {
      // The endpoint caps at 9 tests per call — chunk sequentially.
      for (let i = 0; i < stale.length; i += 9) {
        if (gen !== generation.current) return;
        const chunk = stale.slice(i, i + 9);
        try {
          const data = await ccmApi.resolveQuestHomeboundDx(elationId, chunk.map(s => s.name));
          if (gen !== generation.current) return;
          const answers = (data && data.tests) || [];
          setRows(rs => rs.map(r => {
            const idx = chunk.findIndex(s => s.id === r.id);
            if (idx === -1) return r;
            if (String(r.name || '').trim() !== chunk[idx].name) return r; // renamed since — its own pass is coming
            const resolution = answers[idx] && answers[idx].resolution;
            const chosen = resolution && resolution.code
              ? { code: resolution.code, description: resolution.description || null, status: resolution.status || 'unverified', confidence: resolution.confidence || null }
              : null;
            return { ...r, dxPending: false, dxResolvedForName: chunk[idx].name, dx: resolution || null, chosenDx: chosen };
          }));
        } catch (e) {
          if (gen !== generation.current) return;
          if (e && e.status === 403) { onForbidden(); return; }
          if (e && e.status === 404) {
            // Per-practice flag off (or pre-feature server): quiet manual mode.
            setDxAvailable(false);
            setRows(rs => rs.map(r => ({ ...r, dx: null, dxResolvedForName: null, dxPending: false, chosenDx: null })));
            return;
          }
          // Transient hiccup: quiet manual fallback; the next edit retries.
          setRows(rs => rs.map(r => (r.dxPending ? { ...r, dxPending: false } : r)));
          return;
        }
      }
    }, 600);
  }, [dxAvailable, elationId, setRows, onForbidden]);

  // Patient unpinned/changed: every chip described the OLD chart — drop them
  // all, then (with a chart pinned) resolve the current names fresh.
  useEffect(() => {
    generation.current++;
    if (timer.current) clearTimeout(timer.current);
    const cleared = rowsRef.current.map(r => ({ ...r, dx: null, dxResolvedForName: null, dxPending: false, chosenDx: null }));
    setRows(cleared);
    if (elationId) schedule(cleared);
  }, [elationId, schedule, setRows]);

  return { dxActive: dxAvailable && !!elationId, scheduleDx: schedule };
}

// The chip under a test-name row: pending spinner → chosen chip (confidence
// dot + "CODE — description" + status badge, with an alternates <select>) →
// or the honest "enter manually" hint when the resolver found nothing.
function DxChips({ row, onPick, onClear, disabled }) {
  if (row.dxPending) {
    return <div className="laborders-dx-pending">Finding a covered diagnosis…</div>;
  }
  const candidates = (row.dx && Array.isArray(row.dx.candidates)) ? row.dx.candidates : [];
  if (row.chosenDx) {
    const badge = statusBadge(row.chosenDx.status);
    return (
      <div className="laborders-dx">
        <span className={`laborders-dx-chip conf-${row.chosenDx.confidence || 'low'}`}>
          <span className="laborders-dx-dot" aria-hidden="true" />
          {row.chosenDx.description ? `${row.chosenDx.code} — ${row.chosenDx.description}` : row.chosenDx.code}
        </span>
        <span className={`laborders-badge tone-${badge.tone}`}>{badge.text}</span>
        {candidates.length > 0 ? (
          <select className="laborders-dx-alt" aria-label="Choose an alternate diagnosis" value={row.chosenDx.code}
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.target.value === '__clear__') onClear();
                    else {
                      const c = candidates.find(x => x.code === e.target.value);
                      if (c) onPick(c);
                    }
                  }}>
            {!candidates.some(c => c.code === row.chosenDx.code) && (
              <option value={row.chosenDx.code}>{row.chosenDx.code}</option>
            )}
            {candidates.map(c => <option key={c.code} value={c.code}>{dxCandidateLabel(c)}</option>)}
            <option value="__clear__">Clear — enter manually</option>
          </select>
        ) : (
          <button type="button" className="btn ghost sm" onClick={onClear} disabled={disabled}>Clear</button>
        )}
      </div>
    );
  }
  if (row.dx && String(row.name || '').trim()) {
    return (
      <div className="laborders-dx">
        <span className="laborders-dx-none">No covered diagnosis found on the chart — enter the ICD-10 manually.</span>
        {candidates.length > 0 && (
          <select className="laborders-dx-alt" aria-label="Choose a diagnosis" value=""
                  disabled={disabled}
                  onChange={(e) => {
                    const c = candidates.find(x => x.code === e.target.value);
                    if (c) onPick(c);
                  }}>
            <option value="" disabled>Choose a diagnosis…</option>
            {candidates.map(c => <option key={c.code} value={c.code}>{dxCandidateLabel(c)}</option>)}
          </select>
        )}
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Order set chips (shared by all three modes)
// ---------------------------------------------------------------------------

// Scoped to the CURRENT lab selection; hidden entirely when the /sets proxy
// is unavailable (fail-soft) or no lab is picked. Tap a chip → import the
// set's TEST LIST ONLY (the server already stripped dx + fasting). The ⋯
// affordance opens rename/delete — both practice-wide in Elation, delete
// behind the practice-wide confirm. Trailing chip saves the current tests as
// a new set (tests only).
function OrderSetChips({ available, sets, vendorId, vendorLabel, disabled, setBusy, setErrors, currentTests, onImport, onSave, onRename, onDelete }) {
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [menuFor, setMenuFor] = useState(null);   // set id with the ⋯ menu open
  const [renameFor, setRenameFor] = useState(null); // { id, name } being renamed
  const [renameText, setRenameText] = useState('');

  if (!available || !vendorId) return null;
  const matching = setsForVendor(sets, vendorId);
  const tests = currentTests();

  async function confirmDelete(set) {
    setMenuFor(null);
    const ok = await confirmDialog({
      title: `Delete "${set.name}"?`,
      body: 'Practice-wide — this deletes the set in Elation for everyone.',
      confirmLabel: 'Delete for the whole practice',
      danger: true,
    });
    if (ok) onDelete(set.id);
  }

  function commitSave() {
    const name = saveName.trim();
    if (!name) return;
    setSaving(false); setSaveName('');
    onSave(name, tests);
  }
  function commitRename() {
    const name = renameText.trim();
    const target = renameFor;
    setRenameFor(null); setRenameText('');
    if (target && name && name !== target.name) onRename(target.id, name);
  }

  return (
    <div className="laborders-sets">
      <div className="field-label">Order sets {setBusy ? <span className="laborders-muted">· working…</span> : null}</div>
      {matching.length === 0 && (
        <div className="laborders-muted">No sets for {vendorLabel} yet — fill the form and save one.</div>
      )}
      <div className="laborders-set-chips">
        {matching.map((set) => (
          <span key={set.id} className="laborders-set-chip-wrap">
            <button type="button" className="laborders-set-chip" disabled={disabled || setBusy}
                    title={`Import ${set.name} (${set.tests.length} tests — tests only)`}
                    onClick={() => onImport(set)}>
              {set.name} <span className="laborders-set-count">{set.tests.length}</span>
            </button>
            <button type="button" className="laborders-set-menu-btn" aria-label={`Rename or delete ${set.name}`}
                    aria-expanded={menuFor === set.id} disabled={disabled || setBusy}
                    onClick={() => setMenuFor(menuFor === set.id ? null : set.id)}>⋯</button>
            {menuFor === set.id && (
              <span className="laborders-set-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenuFor(null); setRenameFor({ id: set.id, name: set.name }); setRenameText(set.name); }}>Rename…</button>
                <button type="button" role="menuitem" className="danger" onClick={() => confirmDelete(set)}>Delete…</button>
              </span>
            )}
          </span>
        ))}
        {!saving && (
          <button type="button" className="laborders-set-chip save" disabled={disabled || setBusy || tests.length === 0}
                  title="Saves the current tests (tests only — no diagnoses, no fasting) as a practice-wide order set in Elation."
                  onClick={() => { setSaveName(''); setSaving(true); }}>
            + Save current as set…
          </button>
        )}
      </div>
      {saving && (
        <div className="laborders-set-editor">
          <input type="text" autoFocus value={saveName} placeholder="Set name" aria-label="New order set name"
                 onChange={(e) => setSaveName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitSave(); } else if (e.key === 'Escape') { setSaving(false); setSaveName(''); } }} />
          <button type="button" className="btn sm primary" disabled={!saveName.trim()} onClick={commitSave}>Save</button>
          <button type="button" className="btn sm" onClick={() => { setSaving(false); setSaveName(''); }}>Cancel</button>
          <span className="laborders-muted">Tests only — no diagnoses, no fasting. Practice-wide in Elation.</span>
        </div>
      )}
      {renameFor && (
        <div className="laborders-set-editor">
          <input type="text" autoFocus value={renameText} aria-label="Rename order set"
                 onChange={(e) => setRenameText(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitRename(); } else if (e.key === 'Escape') { setRenameFor(null); } }} />
          <button type="button" className="btn sm primary" disabled={!renameText.trim()} onClick={commitRename}>Rename</button>
          <button type="button" className="btn sm" onClick={() => setRenameFor(null)}>Cancel</button>
          <span className="laborders-muted">Practice-wide — this edits the set in Elation for everyone.</span>
        </div>
      )}
      {setErrors.length > 0 && (
        <ul className="quest-errors laborders-errors">{setErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success pane (all modes)
// ---------------------------------------------------------------------------

function SuccessPane({ result, onReset }) {
  let title; const lines = []; let footnote = null;
  if (result.kind === 'ereq') {
    title = 'Draft created';
    if (result.orderId != null) lines.push(['Elation order #', String(result.orderId)]);
    if (result.vendorName) lines.push(['Lab', result.vendorName]);
    footnote = 'The unsigned order is waiting in the patient’s chart — sign and e-order it in Elation.';
  } else if (result.kind === 'print') {
    title = result.faxed ? `Faxed to ${result.recipient || 'the lab'}` : 'Requisition draft saved';
    if (result.labOrderId != null) lines.push(['Requisition #', String(result.labOrderId)]);
    if (result.reportId != null) lines.push(['Report #', String(result.reportId)]);
    if (result.letterId != null) lines.push(['Letter #', String(result.letterId)]);
    footnote = result.faxed
      ? 'The requisition was filed to Elation and faxed to the lab.'
      : 'Nothing was faxed — the unsigned requisition letter is waiting in Elation.';
  } else {
    title = result.faxed ? 'Faxed to Quest' : 'Draft created';
    if (result.reportId != null) lines.push(['Report #', String(result.reportId)]);
    if (result.letterId != null) lines.push(['Letter #', String(result.letterId)]);
    if (!result.faxed) footnote = 'The order was uploaded to the chart but hasn’t been faxed — finish it from Elation.';
  }
  return (
    <div className="laborders-success quest-result ok">
      <div className="laborders-success-title">✓ {title}</div>
      {lines.map(([k, v]) => <div key={k} className="laborders-success-row"><span>{k}</span><strong>{v}</strong></div>)}
      {(result.warnings || []).map((w, i) => <div key={i} className="laborders-warning">⚠ {w}</div>)}
      {footnote && <p className="sub">{footnote}</p>}
      <button type="button" className="btn primary" onClick={onReset}>Place another order</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modes 1 & 2 — the shared lab-order form (fork at the submit gate only)
// ---------------------------------------------------------------------------

function LabOrderForm({ mode, vendors, vendorsError, onRetryVendors, defaultFasting, setsCtx, onForbidden }) {
  const [rows, setRows] = useState(() => [emptyLabRow()]);
  const [vendorId, setVendorId] = useState('');
  const [fasting, setFasting] = useState('');  // '' → provider default (server applies it)
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);
  // e-req review step (mode 1): summary + required AOE answers.
  const [reviewing, setReviewing] = useState(false);
  const [aoeDrafts, setAoeDrafts] = useState([]);
  // Print preview (mode 2): null | {loading} | {blocked:[..]} |
  // {url, recipientName, fax, warnings, sendErrors:[..]}
  const [preview, setPreview] = useState(null);
  const [faxEdit, setFaxEdit] = useState('');
  // Preview↔send draft-reuse pointer ({elationOrderId, hash}) — echoed back so
  // the send (and any re-preview/retry) reuses the same unsigned Elation draft.
  const printPrior = useRef(null);

  const pin = usePatientPin(busy);
  const { dxActive, scheduleDx } = useDxResolver(pin.pinnedPatient, rows, setRows, onForbidden);

  const vendorList = vendors ? (mode === 'ereq' ? vendors.eReq : vendors.print) : [];
  const vendor = vendorList.find(v => v.vendorId === vendorId) || null;
  const vendorName = vendor ? vendor.vendorName : 'the lab';

  // Auto-pick a practice's only lab; drop a selection that vanished.
  useEffect(() => {
    if (vendorId && !vendorList.some(v => v.vendorId === vendorId)) setVendorId('');
    if (!vendorId && vendorList.length === 1) setVendorId(vendorList[0].vendorId);
  }, [vendors]); // eslint-disable-line react-hooks/exhaustive-deps

  function updateRows(next) { setRows(next); scheduleDx(next); }

  function updateRow(id, key, val) {
    const next = rows.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [key]: val };
      // Editing a set-imported row's name demotes it to free text — an id
      // must never describe a different test.
      if (key === 'name' && r.testId) {
        updated.testId = null; updated.code = null; updated.compendium = null; updated.questions = [];
      }
      return updated;
    });
    if (key === 'name') updateRows(next); else setRows(next);
  }
  function addRow() { setRows(rs => [...rs, emptyLabRow()]); }
  function removeRow(id) { if (rows.length > 1) setRows(rows.filter(r => r.id !== id)); }
  function pickDx(id, candidate) {
    setRows(rs => rs.map(r => (r.id === id
      ? { ...r, chosenDx: { code: candidate.code, description: candidate.description || null, status: candidate.status || 'unverified', confidence: pickedConfidence(candidate) } }
      : r)));
  }
  function clearDx(id) { setRows(rs => rs.map(r => (r.id === id ? { ...r, chosenDx: null } : r))); }
  function importSet(set) { updateRows(importSetIntoRows(rows, set, labRowFromSetTest)); }

  function orderBody() {
    return {
      elationPatientId: Number(pin.pinnedPatient.elationId),
      vendorId,
      tests: buildOrderTests(rows),
      ...(fasting ? { fastingMethod: fasting } : {}),
    };
  }

  // The review gate: local validation only; mode 1 advances to the summary
  // pane (+ staged AOE answers), mode 2 opens the mandatory PDF preview.
  function review() {
    const v = validateLabForm({ pinnedPatient: pin.pinnedPatient, vendorId, rows });
    if (!v.ok) { setErrors(v.errors); return; }
    setErrors([]);
    if (mode === 'ereq') {
      setAoeDrafts(requiredAOE(rows).map((a, i) => ({ key: `${a.testId}-${a.question.id}-${i}`, ...a, value: '' })));
      setReviewing(true);
    } else {
      openPreview();
    }
  }

  // Mode 1 — POST /ereq (internal unsigned draft; preview-rule EXEMPT:
  // signature/e-order happen in Elation, nothing leaves the chart from here).
  async function submitEReq() {
    if (busy) return;
    const blank = aoeDrafts.filter(d => !d.value.trim());
    if (blank.length) { setErrors(blank.map(d => `Answer "${d.question.text}" for ${d.testName}.`)); return; }
    setBusy(true); setErrors([]);
    try {
      const res = await ccmApi.createLabEReq({
        ...orderBody(),
        aoeAnswers: aoeDrafts.length
          ? aoeDrafts.map(d => ({ question: d.question.id, test: d.testId, value: d.value.trim() }))
          : undefined,
      });
      // `warnings` = the route's O8 disclosure (an order-level code a test
      // already carries is not repeated for the whole order). SuccessPane
      // renders it for every result kind, so forward it as the print path does.
      setResult({ kind: 'ereq', orderId: res.orderId, vendorName: res.vendorName, warnings: res.warnings || [] });
      setReviewing(false);
    } catch (e) {
      if (e && e.status === 403) return onForbidden();
      setErrors(errorLines(e, 'The order couldn’t be created.'));
    } finally { setBusy(false); }
  }

  // Mode 2 — POST /print/preview: renders the ACTUAL requisition (creating/
  // reusing the unsigned Elation draft whose id prints as the requisition
  // number). The ONLY send buttons render under this document.
  async function openPreview() {
    setPreview({ loading: true });
    try {
      const res = await ccmApi.labPrintPreview({ ...orderBody(), ...(printPrior.current ? { prior: printPrior.current } : {}) });
      if (!res || !res.pdfBase64) { setPreview({ blocked: ['The requisition preview came back empty — nothing was sent. Try again.'] }); return; }
      const url = base64PdfUrl(res.pdfBase64);
      if (!url) { setPreview({ blocked: ['The requisition preview could not be rendered — nothing was sent.'] }); return; }
      if (res.labOrderId) printPrior.current = { elationOrderId: res.labOrderId, hash: res.hash || null };
      setPreview({ url, recipientName: res.recipientName || null, fax: res.fax || null, warnings: res.warnings || [], sendErrors: [] });
      setFaxEdit(res.fax || '');
    } catch (e) {
      if (e && e.status === 403) return onForbidden();
      setPreview({ blocked: errorLines(e, 'The requisition couldn’t be rendered.') });
    }
  }

  function closePreview() {
    if (preview && preview.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  // Mode 2 — the explicit confirm UNDER the rendered document. 'draft' files
  // an unsigned Elation letter; 'fax' signs + transmits NOW.
  async function confirmPrintSend(sendMode) {
    if (busy) return;
    const previewDigits = String((preview && preview.fax) || '').replace(/\D/g, '');
    const digits = faxEdit.replace(/\D/g, '');
    let faxOverride;
    if (sendMode === 'fax' && digits && digits !== previewDigits) {
      if (digits.length < 10 || digits.length > 11) {
        setPreview(p => ({ ...p, sendErrors: ['Enter a 10-11 digit fax number (or restore the configured one).'] }));
        return;
      }
      faxOverride = digits;
    }
    setBusy(true);
    try {
      const res = await ccmApi.labPrintSend({
        ...orderBody(),
        ...(printPrior.current ? { prior: printPrior.current } : {}),
        mode: sendMode,
        ...(faxOverride ? { faxOverride } : {}),
      });
      closePreview();
      setResult({ kind: 'print', ...res });
    } catch (e) {
      if (e && e.status === 403) return onForbidden();
      // A partial failure (409) may still have created the draft — keep the
      // pointer so a retry reuses it instead of duplicating.
      if (e && e.body && e.body.labOrderId) printPrior.current = { elationOrderId: e.body.labOrderId, hash: e.body.hash || null };
      setPreview(p => (p && p.url ? { ...p, sendErrors: errorLines(e, 'The requisition couldn’t be sent.') } : p));
    } finally { setBusy(false); }
  }

  // "Place another order" — vendor and fasting survive (per-provider working
  // set); everything else clears.
  function reset() {
    setRows([emptyLabRow()]);
    pin.clearPin();
    setErrors([]); setResult(null); setReviewing(false); setAoeDrafts([]);
    closePreview(); printPrior.current = null;
  }

  if (result) return <SuccessPane result={result} onReset={reset} />;

  if (reviewing) {
    const real = rows.filter(r => String(r.name || '').trim());
    return (
      <div className="laborders-review">
        <div className="field-label">Review before drafting</div>
        <div className="laborders-summary">
          <div className="laborders-success-row"><span>Patient</span><strong>{pin.pinnedPatient ? `${pin.pinnedPatient.firstName} ${pin.pinnedPatient.lastName}` : '—'}</strong></div>
          <div className="laborders-success-row"><span>Lab</span><strong>{vendorName}</strong></div>
          <div className="laborders-success-row"><span>Fasting</span><strong>{fasting ? fastingLabel(fasting) : 'Provider default'}</strong></div>
          <div className="laborders-success-row"><span>Tests</span>
            <strong>{real.map(r => {
              const codes = rowCodes(r);
              return codes.length ? `${r.name.trim()} (${codes.join(', ')})` : r.name.trim();
            }).join('; ')}</strong>
          </div>
        </div>
        {aoeDrafts.length > 0 && (
          <div className="laborders-aoe">
            <div className="field-label">Lab questions (required)</div>
            {aoeDrafts.map((d) => (
              <div key={d.key} className="laborders-aoe-row">
                <div className="laborders-aoe-q">{d.testName}: {d.question.text}</div>
                <AoeInput draft={d} disabled={busy}
                          onChange={(value) => setAoeDrafts(ds => ds.map(x => (x.key === d.key ? { ...x, value } : x)))} />
              </div>
            ))}
          </div>
        )}
        {errors.length > 0 && <ul className="quest-errors laborders-errors">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
        <p className="sub">The draft lands unsigned in the patient&rsquo;s Elation chart. You sign and e-order it in Elation — nothing is transmitted from here.</p>
        <div className="quest-actions">
          <button type="button" className="btn" disabled={busy} onClick={() => setReviewing(false)}>Back to edit</button>
          <button type="button" className="btn primary" disabled={busy} onClick={submitEReq}>
            {busy ? 'Creating draft…' : 'Create draft in Elation'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="laborders-form">
      <p className="sub">
        {mode === 'ereq'
          ? 'Drafts an unsigned lab order into the patient’s Elation chart for a connected lab — you sign and e-order it in Elation. Nothing is sent from here.'
          : 'Builds the requisition for a print-only lab: review the actual form, then save it as a draft or fax it to the lab.'}
      </p>

      {/* Lab + fasting */}
      <div className="field">
        <div className="field-label">Lab</div>
        {!vendors && !vendorsError && <div className="laborders-muted">Loading the practice&rsquo;s labs…</div>}
        {vendorsError && (
          <div className="laborders-warning">Couldn&rsquo;t load the practice&rsquo;s labs. <button type="button" className="btn sm" onClick={onRetryVendors}>Retry</button></div>
        )}
        {vendors && vendorList.length === 0 && (
          <div className="laborders-warning">
            {mode === 'ereq'
              ? 'No labs with a live electronic connection are configured in Elation for this practice.'
              : 'No print-only labs are configured. Add the lab in Elation, then its account # and fax under Settings → lab fax.'}
          </div>
        )}
        {vendorList.length > 0 && (
          <select value={vendorId} aria-label="Lab vendor" disabled={busy} onChange={(e) => setVendorId(e.target.value)}>
            <option value="">Choose a lab…</option>
            {vendorList.map(v => <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>)}
          </select>
        )}
        <div className="laborders-fasting">
          <label>
            Fasting{' '}
            <select value={fasting} disabled={busy} onChange={(e) => setFasting(e.target.value)}>
              <option value="">{defaultFasting ? `Provider default (${fastingLabel(defaultFasting)})` : 'Provider default'}</option>
              {FASTING_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      <OrderSetChips
        available={setsCtx.available} sets={setsCtx.sets}
        vendorId={vendorId || null} vendorLabel={vendorName}
        disabled={busy} setBusy={setsCtx.busy} setErrors={setsCtx.errors}
        currentTests={() => currentSetTests(rows)}
        onImport={importSet}
        onSave={(name, tests) => setsCtx.save(name, vendorId, tests)}
        onRename={setsCtx.rename} onDelete={setsCtx.remove}
      />

      {pin.view}

      {/* Test rows */}
      <div className="quest-tests">
        <div className="field-label">Tests</div>
        {rows.map((r) => (
          <div className="laborders-test-row" key={r.id}>
            <div className="laborders-test-main">
              <input placeholder="Test name" value={r.name} aria-label="Test name"
                     onChange={(e) => updateRow(r.id, 'name', e.target.value)} disabled={busy} />
              <input placeholder="ICD-10 (comma-sep)" value={r.icd10} aria-label="ICD-10 codes"
                     onChange={(e) => updateRow(r.id, 'icd10', e.target.value)} disabled={busy} />
              <button type="button" className="btn ghost sm" aria-label="Remove this test"
                      onClick={() => removeRow(r.id)} disabled={busy || rows.length === 1}>✕</button>
            </div>
            {r.testId && (
              <div className="laborders-muted laborders-set-badge">
                From order set{r.code ? ` · #${r.code}` : ''}{(r.questions || []).some(q => q.required) ? ' · has AOE questions' : ''}
              </div>
            )}
            {dxActive && <DxChips row={r} disabled={busy} onPick={(c) => pickDx(r.id, c)} onClear={() => clearDx(r.id)} />}
          </div>
        ))}
        <button type="button" className="btn sm" onClick={addRow} disabled={busy}>+ Add test</button>
      </div>

      {errors.length > 0 && <ul className="quest-errors laborders-errors">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}

      <div className="quest-actions">
        <button type="button" className="btn primary" disabled={busy || !!(preview && preview.url)} onClick={review}>
          {mode === 'ereq' ? 'Review draft' : 'Review requisition…'}
        </button>
      </div>

      {/* Mode 2 — the MANDATORY real-PDF preview. The only Save draft / Fax
          buttons in this mode render UNDER the rendered document; a blocked
          render is an honest dead-end with no send path at all. */}
      {mode === 'print' && preview && (
        <div className="laborders-preview">
          {preview.loading && <div className="laborders-muted">Rendering the requisition…</div>}
          {preview.blocked && (
            <div role="alert" className="laborders-blocked">
              <strong>Couldn&rsquo;t render the requisition.</strong> {preview.blocked.join(' ')} Nothing was faxed — the order can only be sent after reviewing the actual form.
              <div><button type="button" className="btn sm" onClick={closePreview}>Back to the form</button></div>
            </div>
          )}
          {preview.url && (
            <>
              <iframe title="Lab requisition preview" src={preview.url} className="laborders-pdf" />
              <div className="laborders-consent">
                <div>Will fax to <b>{preview.recipientName || vendorName}</b>{pin.pinnedPatient ? <> — for <b>{pin.pinnedPatient.firstName} {pin.pinnedPatient.lastName}</b></> : null}</div>
                <div>This is the exact form {vendorName} receives. Faxing transmits it to an outside party immediately; saving a draft files it unsigned in Elation instead.</div>
                <label className="laborders-fax-override">
                  Fax number{' '}
                  <input type="text" value={faxEdit} placeholder="fax number" aria-label="Destination fax number"
                         onChange={(e) => setFaxEdit(e.target.value)} disabled={busy} />
                  {preview.fax && faxEdit.replace(/\D/g, '') !== String(preview.fax).replace(/\D/g, '') && (
                    <span className="laborders-warning-inline">(one-off override)</span>
                  )}
                </label>
                {requiredAOE(rows).length > 0 && (
                  <div className="laborders-warning">AOE questions will be answered at sign-time in Elation.</div>
                )}
                {(preview.warnings || []).map((w, i) => <div key={i} className="laborders-warning">⚠ {w}</div>)}
                {(preview.sendErrors || []).length > 0 && (
                  <ul className="quest-errors laborders-errors">{preview.sendErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                )}
                <div className="quest-actions">
                  <button type="button" className="btn" disabled={busy} onClick={closePreview}>Cancel</button>
                  <button type="button" className="btn" disabled={busy} onClick={() => confirmPrintSend('draft')}>Save draft</button>
                  <button type="button" className="btn primary" disabled={busy} onClick={() => confirmPrintSend('fax')}>
                    {busy ? 'Sending…' : `Fax to ${vendorName}`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// One AOE answer input, shaped by the question type: choices → select,
// yes/no → select Y/N, numeric → decimal input, else free text.
function AoeInput({ draft, disabled, onChange }) {
  const q = draft.question;
  if (Array.isArray(q.choices) && q.choices.length) {
    return (
      <select value={draft.value} disabled={disabled} aria-label={q.text} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {q.choices.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  if (q.type === 'y_or_n') {
    return (
      <select value={draft.value} disabled={disabled} aria-label={q.text} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        <option value="Y">Yes</option>
        <option value="N">No</option>
      </select>
    );
  }
  return (
    <input type="text" value={draft.value} placeholder="Answer" aria-label={q.text}
           inputMode={q.type === 'numeric' ? 'decimal' : undefined}
           disabled={disabled} onChange={(e) => onChange(e.target.value)} />
  );
}

// ---------------------------------------------------------------------------
// Mode 3 — Quest home draw (the original homebound form + chips + preview gate)
// ---------------------------------------------------------------------------

function QuestHomeForm({ vendors, setsCtx, onForbidden }) {
  const [tests, setTests] = useState(() => [emptyQuestRow()]);
  const [homeboundAttested, setHomeboundAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);
  // Preview-then-send (app-wide rule): the ONLY path to the fax runs through
  // the rendered document. /api/quest-homebound/preview is side-effect-free.
  const [preview, setPreview] = useState(null); // null | {loading} | {blocked:[..]} | {url, recipientName, fax, sendErrors}

  // The confirm under the preview rebuilds payload() from LIVE state, so anything
  // still editable while the PDF is on screen could be faxed without ever being
  // shown (codex C1, adjudicated r2 #1). While a preview is open — including
  // while it renders, since the document is built from state at that click — the
  // form is FROZEN: Cancel / "Back to the form" close it and editing resumes.
  // A blocked preview rendered no document, so it leaves the form editable.
  const previewOpen = !!preview && !preview.blocked;
  const pin = usePatientPin(busy || previewOpen);
  const { dxActive, scheduleDx } = useDxResolver(pin.pinnedPatient, tests, setTests, onForbidden);

  const questVendorId = vendors ? vendors.questVendorId : null;

  function updateRows(next) { setTests(next); scheduleDx(next); }
  function updateTest(id, key, val) {
    const next = tests.map(t => (t.id === id ? { ...t, [key]: val } : t));
    if (key === 'name') updateRows(next); else setTests(next);
  }
  function addRow() { setTests(ts => [...ts, emptyQuestRow()]); }
  function removeRow(id) { if (tests.length > 1) setTests(tests.filter(t => t.id !== id)); }
  function pickDx(id, candidate) {
    setTests(ts => ts.map(t => (t.id === id
      ? { ...t, chosenDx: { code: candidate.code, description: candidate.description || null, status: candidate.status || 'unverified', confidence: pickedConfidence(candidate) } }
      : t)));
  }
  function clearDx(id) { setTests(ts => ts.map(t => (t.id === id ? { ...t, chosenDx: null } : t))); }
  function importSet(set) { updateRows(importSetIntoRows(tests, set, questRowFromSetTest)); }

  function payload() {
    return buildPayload({ pinnedPatient: pin.pinnedPatient, homeboundAttested, tests: questTestsWithChips(tests) });
  }

  // Review & fax: local validation first (errors inline, exactly as before),
  // then the preview renders the real PDF — the fax can ONLY be confirmed
  // under that document.
  async function review() {
    const state = { pinnedPatient: pin.pinnedPatient, homeboundAttested, tests: questTestsWithChips(tests) };
    const v = validateForm(state);
    if (!v.ok) { setErrors(v.errors); setResult(null); return; }
    setErrors([]);
    setPreview({ loading: true });
    try {
      // Freezing the controls is not enough on its own: useDxResolver writes
      // `chosenDx` from a debounce timer + a network call, and rowCodes folds that
      // code into the payload's ICD-10 — `disabled` cannot stop a timer. So the
      // body this PDF is rendered from travels WITH the rendered PDF, in the same
      // state object, and the confirm sends that. Not a ref: a second Review while
      // the first is still loading would overwrite a shared ref before its await
      // resolved, and the provider could be shown PDF A while body B was sent.
      const body = payload();
      const res = await ccmApi.previewQuestHomeboundOrder(body);
      if (!res || !res.pdfBase64) { setPreview({ blocked: ['The requisition preview came back empty — nothing was filed or faxed.'] }); return; }
      const url = base64PdfUrl(res.pdfBase64);
      if (!url) { setPreview({ blocked: ['The requisition preview could not be rendered — nothing was filed or faxed.'] }); return; }
      setPreview({ url, body, recipientName: res.recipientName || null, fax: res.fax || null, sendErrors: [] });
    } catch (e) {
      if (e && e.status === 403) return onForbidden();
      setPreview({ blocked: errorLines(e, 'The requisition couldn’t be rendered.') });
    }
  }

  function closePreview() {
    if (preview && preview.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  // The explicit confirm under the rendered document — the real send. It sends
  // the body THIS displayed PDF was rendered from, never a rebuild of live state.
  async function confirmFax() {
    if (busy) return;
    // No rendered document + body = nothing was reviewed for this confirm. Close
    // and make them review again rather than fax something unseen.
    if (!preview || !preview.url || !preview.body) { closePreview(); return; }
    setBusy(true);
    try {
      const res = await ccmApi.submitQuestHomeboundOrder(preview.body);
      closePreview();
      setResult(res);
    } catch (e) {
      if (e && e.status === 403) return onForbidden();
      setPreview(p => (p && p.url ? { ...p, sendErrors: errorLines(e, 'Submit failed') } : p));
    } finally { setBusy(false); }
  }

  function reset() {
    setTests([emptyQuestRow()]);
    pin.clearPin();
    setHomeboundAttested(false);
    setErrors([]); setResult(null); closePreview();
  }

  if (result && result.ok) {
    return <SuccessPane result={{ kind: 'quest', faxed: result.faxed, reportId: result.reportId, letterId: result.letterId }} onReset={reset} />;
  }

  return (
    <div className="laborders-form">
      {/* A home draw is a standing order OR a one-time draw (F10: the filed
          report is titled for whichever it is) — the subtitle used to name only
          the standing kind. The 6-month cap is the standing order's. */}
      <p className="sub">Mobile-phlebotomy home-draw order (standing or one-time). Patient must be homebound. Standing orders may not exceed 6 months.</p>

      <OrderSetChips
        available={setsCtx.available} sets={setsCtx.sets}
        vendorId={questVendorId} vendorLabel="Quest"
        disabled={busy || previewOpen} setBusy={setsCtx.busy} setErrors={setsCtx.errors}
        currentTests={() => currentSetTests(tests)}
        onImport={importSet}
        onSave={(name, tsts) => setsCtx.save(name, questVendorId, tsts)}
        onRename={setsCtx.rename} onDelete={setsCtx.remove}
      />

      {pin.view}

      {/* Test rows — the original quest fields, unchanged */}
      <div className="quest-tests">
        <div className="field-label">Tests</div>
        {tests.map((t) => (
          <div className="laborders-test-row" key={t.id}>
            <div className="quest-test-row laborders-quest-grid">
              <input placeholder="Test name" value={t.name} aria-label="Test name" onChange={(e) => updateTest(t.id, 'name', e.target.value)} disabled={busy || previewOpen} />
              <input placeholder="Test #" value={t.testNumber} aria-label="Test number" onChange={(e) => updateTest(t.id, 'testNumber', e.target.value)} disabled={busy || previewOpen} />
              <input placeholder="ICD-10 (comma-sep)" value={t.icd10} aria-label="ICD-10 codes" onChange={(e) => updateTest(t.id, 'icd10', e.target.value)} disabled={busy || previewOpen} />
              <label className="quest-fasting"><input type="checkbox" checked={t.fasting} onChange={(e) => updateTest(t.id, 'fasting', e.target.checked)} disabled={busy || previewOpen} /> Fasting</label>
              <FrequencyCell value={t.frequency} disabled={busy || previewOpen} onChange={(v) => updateTest(t.id, 'frequency', v)} />
              <input type="date" value={t.startDate} aria-label="Start date" onChange={(e) => updateTest(t.id, 'startDate', e.target.value)} disabled={busy || previewOpen} />
              <input type="date" value={t.finishDate} aria-label="Finish date" onChange={(e) => updateTest(t.id, 'finishDate', e.target.value)} disabled={busy || previewOpen} />
              <button type="button" className="btn ghost sm" aria-label="Remove this test" onClick={() => removeRow(t.id)} disabled={busy || previewOpen || tests.length === 1}>✕</button>
            </div>
            {dxActive && <DxChips row={t} disabled={busy || previewOpen} onPick={(c) => pickDx(t.id, c)} onClear={() => clearDx(t.id)} />}
          </div>
        ))}
        <button type="button" className="btn sm" onClick={addRow} disabled={busy || previewOpen}>+ Add test</button>
      </div>

      {/* Homebound attestation */}
      <label className="quest-attest">
        <input type="checkbox" checked={homeboundAttested} onChange={(e) => setHomeboundAttested(e.target.checked)} disabled={busy || previewOpen} />
        I attest this patient is homebound.
      </label>

      {errors.length > 0 && <ul className="quest-errors laborders-errors">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}

      <div className="quest-actions">
        {/* Single-flight: `previewOpen` counts the LOADING preview too, so a
            second Review cannot race the first and swap the body under the PDF. */}
        <button type="button" className="btn primary" disabled={busy || previewOpen} onClick={review}>
          Review &amp; fax to Quest
        </button>
      </div>

      {/* The MANDATORY real-PDF preview — the only Fax button lives under it. */}
      {preview && (
        <div className="laborders-preview">
          {preview.loading && <div className="laborders-muted">Rendering the requisition…</div>}
          {preview.blocked && (
            <div role="alert" className="laborders-blocked">
              <strong>Couldn&rsquo;t render the requisition.</strong> {preview.blocked.join(' ')} Nothing was filed or faxed — the order can only be sent after reviewing the actual form.
              <div><button type="button" className="btn sm" onClick={closePreview}>Back to the form</button></div>
            </div>
          )}
          {preview.url && (
            <>
              <iframe title="Quest requisition preview" src={preview.url} className="laborders-pdf" />
              <div className="laborders-consent">
                <div>Will fax to <b>{preview.recipientName || 'Quest Mobile Phlebotomy'}</b>{preview.fax ? ` · ${preview.fax}` : ''}</div>
                <div>This is the exact form Quest receives. Confirming files it to Elation and faxes it — an outside party gets it immediately.</div>
                {(preview.sendErrors || []).length > 0 && (
                  <ul className="quest-errors laborders-errors">{preview.sendErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                )}
                <div className="quest-actions">
                  <button type="button" className="btn" disabled={busy} onClick={closePreview}>Cancel</button>
                  <button type="button" className="btn primary" disabled={busy} onClick={confirmFax}>
                    {busy ? 'Sending…' : 'Confirm & fax to Quest'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page — mode selector + shared context (vendors / sets / fasting label)
// ---------------------------------------------------------------------------

export function LabOrdersPage() {
  const [mode, setMode] = useState(() => {
    try { return sanitizeMode(localStorage.getItem(MODE_STORAGE_KEY)); } catch { return 'ereq'; }
  });
  const [vendors, setVendors] = useState(null);
  const [vendorsError, setVendorsError] = useState(false);
  const [setsAvailable, setSetsAvailable] = useState(false);
  const [sets, setSets] = useState([]);
  const [setBusy, setSetBusy] = useState(false);
  const [setErrors, setSetErrors] = useState([]);
  const [defaultFasting, setDefaultFasting] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const onForbidden = useCallback(() => setForbidden(true), []);

  async function loadVendors() {
    setVendorsError(false);
    try {
      setVendors(await ccmApi.getLabVendors());
    } catch (e) {
      if (e && e.status === 403) setForbidden(true);
      else setVendorsError(true);
    }
  }

  // GET /sets is fail-soft: ok:true marks a trustworthy list; anything else
  // (including a plain {sets: []}) hides the chips row entirely.
  async function refreshSets() {
    try {
      const r = await ccmApi.listLabOrderSets();
      setSetsAvailable(!!(r && r.ok === true));
      setSets((r && r.sets) || []);
    } catch (e) {
      if (e && e.status === 403) setForbidden(true);
      setSetsAvailable(false); setSets([]);
    }
  }

  useEffect(() => {
    loadVendors();
    refreshSets();
    // Label-only: the server applies the real default at order time.
    ccmApi.getOrderChatWhoami().then(w => setDefaultFasting((w && w.defaultFasting) || null)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function runSetAction(action) {
    if (setBusy) return;
    setSetBusy(true); setSetErrors([]);
    try {
      await action();
      await refreshSets();
    } catch (e) {
      if (e && e.status === 403) setForbidden(true);
      else setSetErrors(errorLines(e, 'The order set action failed.'));
    } finally { setSetBusy(false); }
  }

  const setsCtx = {
    available: setsAvailable, sets, busy: setBusy, errors: setErrors,
    save: (name, vendorId, tests) => runSetAction(() => ccmApi.createLabOrderSet({ name, vendorId: vendorId || undefined, tests })),
    rename: (id, name) => runSetAction(() => ccmApi.renameLabOrderSet(id, name)),
    remove: (id) => runSetAction(() => ccmApi.deleteLabOrderSet(id)),
  };

  function pickMode(next) {
    setMode(next);
    try { localStorage.setItem(MODE_STORAGE_KEY, next); } catch { /* pref only */ }
  }

  if (forbidden) {
    return (
      <div className="quest-page laborders-page">
        <h2>Lab Orders</h2>
        <p className="sub">Your account doesn&rsquo;t have access to place lab orders. Ask your administrator if you think it should.</p>
      </div>
    );
  }

  return (
    <div className="quest-page laborders-page">
      <h2>Lab Orders</h2>

      {/* Mode selector — the choice persists (localStorage); contents don't. */}
      <div className="laborders-modes" role="tablist" aria-label="Order mode">
        {MODES.map((m) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m}
                  className={'laborders-mode' + (mode === m ? ' is-active' : '')}
                  onClick={() => pickMode(m)}>
            {MODE_LABELS[m]}
          </button>
        ))}
      </div>

      {mode === 'questhome' ? (
        <QuestHomeForm key="questhome" vendors={vendors} setsCtx={setsCtx} onForbidden={onForbidden} />
      ) : (
        <LabOrderForm key={mode} mode={mode}
                      vendors={vendors} vendorsError={vendorsError} onRetryVendors={loadVendors}
                      defaultFasting={defaultFasting} setsCtx={setsCtx} onForbidden={onForbidden} />
      )}
    </div>
  );
}
